import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { useCallback, useEffect, useMemo, useState } from "react";
import BoxWrapper from "../../components/BoxWrapper";
import DateInput from "../../components/datePicker";
import Loader from "../../components/Loader";
import Pagination from "../../components/Pagination";
import RenderTable from "../../components/RenderTable";
import { DriversSvg } from "../../components/SideBarMenu";
import {
  DriverProfileStatus,
  DriverStatusLabels,
  RangeEnum,
} from "../../helpers/enums";
import { pluralize } from "../../helpers/general";
import { useGetDriverSessions } from "../../hooks/drivers/useDriverSessions";
import { useDebounce } from "../../hooks/useDebounce";
import { general } from "../../locales/general";
import SimpleDriversTable from "./Activitiestable";
import qs from "qs";
import { useLocation, useNavigate } from "react-router-dom";

export function DriverSessionsList() {
  const { search } = useLocation();
  const queryParams = qs.parse(search.replace(/^\?/, ""));
  const initialPage = parseInt(queryParams.page as string) || 1;
  const initialDate = queryParams.date as string;
  const initialRange = queryParams.range as string;
  const initialStatus = queryParams.status as string;
  const initialNotLoggedIn = queryParams.notLoggedIn as string;
  const navigate = useNavigate();

  const [page, setPage] = useState(initialPage);
  const [date, setDate] = useState<string>(initialDate);
  const [notLogedIn, setNotLogedIn] = useState<number | undefined>(
    initialNotLoggedIn ? parseInt(initialNotLoggedIn, 10) : undefined,
  );
  const [searchQuery, setSearchQuery] = useState(queryParams.query as string);

  // const [notLogedIn, setNotLogedIn] = useState<number | undefined>(undefined);
  // const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"default" | "sessionsCount">("default");
  const [range, setRange] = useState<RangeEnum>(initialRange as RangeEnum);
  const [status, setStatus] = useState<DriverProfileStatus>(
    (initialStatus as DriverProfileStatus) ?? DriverProfileStatus.APPROVED,
  );

  const addQuery = (key: string, value: any) => {
    const newQuery = {
      ...queryParams,
      [key]: value === "" ? undefined : value,
      page: key === "page" ? value : 1,
    };
    const newSearch = qs.stringify(newQuery, { skipNulls: true });
    navigate(`?${newSearch}`, { replace: true });
  };

  useEffect(() => {
    const newQuery = {
      ...queryParams,
      page: page.toString(),
    };
    const newSearch = qs.stringify(newQuery, { skipNulls: true });
    navigate(`?${newSearch}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSortChange = useCallback(
    (newSort: "default" | "sessionsCount") => {
      setSortBy(newSort);
      setPage(1);
      // addQuery("sortBy", newSort);
    },
    [],
  );
  const debouncedQuery = useDebounce(searchQuery, 2000);

  const filter = useMemo(
    () => ({
      date,
      notLogedIn,
      page,
      limit: 10,
      query: debouncedQuery || undefined,
      sortBy: sortBy,
      range,
      status,
    }),
    [date, notLogedIn, page, debouncedQuery, sortBy, range, status],
  );

  const { drivers, isDriverLoading } = useGetDriverSessions("", filter);

  const sessionMap: Record<string, any[]> = {};

  if (drivers?.items) {
    drivers.items.forEach((driver: Driver) => {
      sessionMap[driver.id] = driver.sessions || [];
    });
  }

  const handleDateChange = (newDate: string) => {
    setDate(newDate);
    setPage(1);
    setRange(RangeEnum.all);
    addQuery("date", newDate);
  };

  const handleRangeChange = (newRange: RangeEnum) => {
    setRange(newRange);
    setPage(1);
    setDate("");
    addQuery("range", newRange);
  };

  if (isDriverLoading)
    return (
      <div>
        <Loader />
      </div>
    );

  return (
    <div className="">
      <h4 className="fs-4 fw-bold mb-2">Liste des chauffeurs Sessions</h4>
      <BoxWrapper className="">
        <div className=" d-flex justify-content-between w-100 ">
          <div className="d-flex  gap-3 p-4 w-100">
            {/* Date Picker */}
            <DateInput value={date} onChange={handleDateChange} />
            <div className="mb-5 form-group">
              <label htmlFor="notLoggedIn">Date</label>
              <select
                className="form-select"
                id="range"
                value={range}
                onChange={(e) => handleRangeChange(e.target.value as RangeEnum)}
              >
                <option value="">Tous</option>
                <option value={RangeEnum.TODAY}>Aujourd'hui</option>
                <option value={RangeEnum.THIS_WEEK}>Semaine en cours</option>
                <option value={RangeEnum.THIS_MONTH}>Mois en cours</option>
                <option value={RangeEnum.LAST_3_MONTHS}>3 derniers mois</option>
              </select>
            </div>

            <div
              className=" mb-5 form-group"
              style={{
                width: "160px",
              }}
            >
              <label htmlFor="notLoggedIn">Connectés</label>
              <select
                id="notLoggedIn"
                className="form-select"
                value={notLogedIn || ""}
                onChange={(e) => {
                  const value = e.target.value
                    ? parseInt(e.target.value, 10)
                    : undefined;
                  setNotLogedIn(value);
                  setPage(1);
                  addQuery("notLoggedIn", value);
                }}
              >
                <option value="">Tous</option>
                <option value={1}>Non Connecté</option>
                <option value={2}>Connecté</option>
              </select>
            </div>
            <div
              className="form-group"
              style={{
                width: "130px",
              }}
            >
              <label htmlFor="status">Statut</label>
              <select
                name="status"
                id="status"
                className="form-select w-100"
                onChange={(e) => {
                  setStatus(e.target.value as DriverProfileStatus);
                  setPage(1);
                }}
                value={status}
              >
                <option value="">Tous</option>
                {Object.entries(DriverStatusLabels).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div
              className="search-form"
              style={{
                marginTop: "25px",
                height: "40px",
                border: "1px solid black",
              }}
            >
              <div className="input-field">
                <MagnifyingGlassIcon color="gray" width={20} />
                <input
                  type="text"
                  className="w-100 border-0  outline-none rounded-sm p-1 text-gray fs-6"
                  placeholder="Rechercher"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div
            className="d-flex align-items-center justify-content-center gap-3 bg-gradient-primary p-3"
            style={{ borderTopRightRadius: "8px", alignSelf: "flex-start" }}
          >
            <DriversSvg className="fill-white" />
            <p className="fs-5 fw-bold m-0  text-white text-pre">
              {drivers?.meta?.totalItems}{" "}
              {pluralize("chauffeur", drivers?.meta?.totalItems)}
            </p>
          </div>
        </div>

        <RenderTable
          loading={isDriverLoading}
          render={() =>
            drivers?.items && drivers.items.length > 0 ? (
              <SimpleDriversTable
                drivers={drivers.items}
                sortBy={sortBy}
                onSortChange={handleSortChange}
                date={date}
                range={range}
              />
            ) : (
              <p className="text-center fs-5 my-2">{general.fr.noData}</p>
            )
          }
        />
      </BoxWrapper>

      <Pagination
        currentPage={drivers.meta.currentPage}
        onPageChange={(newPage) => setPage(newPage)}
        siblingCount={1}
        totalCount={drivers.meta.totalPages}
        pageSize={drivers.meta.itemsPerPage}
      />
    </div>
  );
}
